import { SubstepType } from "~/features/configurator/Linear/dtos";
import { isTileStandard } from "~/features/configurator/common/tileUtils";
import * as InchCstics from "~/features/configurator/Shaft/inch/inchCsticConstants";
import { chamfer, radialThread } from "~/features/configurator/Shaft/inch/inchCsticConstants";
import { type SummaryData, type SummaryDataProvider } from "~/features/configurator/Shaft/SummaryDataProvider";
import { formatCsticValueAsNumber } from "~/features/configurator/Shaft/components/useMinMaxCsticLabel";
import { INCH_UNIT, METRIC_UNIT } from "~/features/configurator/Shaft/common/commonShaftConstants";
import type { WithTranslation } from "react-i18next";
import {
  isChamferManualWidth,
  resolveMachiningLabelString,
  resolveShaftToleranceMinmax,
  showChamferedSection,
  showRadialThreadSection,
} from "~/features/configurator/Shaft/shaftConfiguratorHelper";
import type { LinearCstic } from "~/features/configurator/Linear/models";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";

export { getStep1SummaryData } from "~/features/configurator/Shaft/metric/MetricSummaryDataProvider";

interface Step2SummaryData {
  length?: SummaryData; // inch and metric
  numberOfParts?: SummaryData; // inch and metric
  tolerance?: SummaryData;
  left?: SummaryData & {
    chamfer?: ChamferSummaryData;
  };
  middle?: SummaryData & {
    radialThread?: RadialThreadSummaryData;
  };
  right?: SummaryData & {
    chamfer?: ChamferSummaryData;
  };
}

interface ChamferSummaryData {
  angle: SummaryData;
  width: SummaryData;
}
interface RadialThreadSummaryData {
  pdt: SummaryData;
  threadSpacing: SummaryData; // inch and metric
  aL: SummaryData; // inch and metric
  aR: SummaryData; // inch and metric
  threadHoleAmount: SummaryData;
  threadDepth: SummaryData;
}

function formatDualValue({
  inch,
  mm,
  inchDecimals = 4,
  mmDecimals = 2,
}: {
  inch: string | null | undefined;
  mm: string | null | undefined;
  inchDecimals?: number;
  mmDecimals?: number;
}) {
  inch = formatCsticValueAsNumber(inch, inchDecimals);
  mm = formatCsticValueAsNumber(mm, mmDecimals);
  return `${inch}${INCH_UNIT} | ${mm}${METRIC_UNIT}`;
}

export function getStep2SummaryData(summaryProvider: SummaryDataProvider) {
  const t = summaryProvider.t;
  const cstics = getMainKmat({ kmats: summaryProvider.kmats }).cstics;

  const data: Step2SummaryData = {};

  // Length
  const lengthMetric = cstics[InchCstics.SHAFT_LENGTH_METRIC]?.value;
  const lengthInch = cstics[InchCstics.SHAFT_LENGTH_INCH]?.value;
  if (!!lengthMetric && !!lengthInch) {
    data.length = {
      name: t("configurator.shaft.shaftLength.title.default"),
      isStandard: false,
      value: formatDualValue({ inch: lengthInch, mm: lengthMetric }),
    };
  }

  // Number of parts
  const numberOfParts = cstics[InchCstics.SHAFT_NUMBER_OF_PARTS]?.value;
  if (numberOfParts) {
    const csticIndex = cstics[InchCstics.SHAFT_NUMBER_OF_PARTS]?.values.findIndex((v) => v.value === numberOfParts);
    data.numberOfParts = {
      name: t("configurator.shaft.step.2.substep.1.tile.1.title"),
      isStandard: false,
      value: t(`configurator.shaft.step.2.substep.1.option.${csticIndex + 1}`),
    };
  }

  // Tolerance
  const toleranceSubstep = summaryProvider.substeps[SubstepType.ShaftTolerance];
  const selectedTile =
    toleranceSubstep.selectedTile !== null ? toleranceSubstep.tiles[toleranceSubstep.selectedTile] : null;
  if (selectedTile) {
    const minmaxVal = resolveShaftToleranceMinmax(
      ConfiguratorType.InchShaft,
      Object.values(selectedTile.cstics)[0],
      cstics,
      INCH_UNIT,
    );
    const value = t(`configurator.shaft.step.2.substep.2.tile.${toleranceSubstep.selectedTile! + 1}.title`);
    data.tolerance = {
      name: t("configurator.shaft.shaftLengthTolerance.title.default"),
      isStandard: isTileStandard(selectedTile, summaryProvider.kmats),
      value: [minmaxVal, value].join(" "),
    };
  }

  const left: Step2SummaryData["left"] = getMachiningLeft(cstics, t)!;
  const leftCstic = cstics[InchCstics.SHAFT_MACHINING_LEFT];
  if (showChamferedSection(leftCstic.value)) {
    left.chamfer = resolveChamferSection(cstics, t, chamfer.left);
  }
  data.left = left;

  const middle: Step2SummaryData["middle"] = getMachiningMiddle(cstics, t)!;
  const middleCstic = cstics[InchCstics.SHAFT_MACHINING_MIDDLE];
  if (showRadialThreadSection(middleCstic.value)) {
    middle.radialThread = resolveRadialThreadSection(cstics, t, radialThread);
  }
  data.middle = middle;

  const right: Step2SummaryData["right"] = getMachiningRight(cstics, t)!;
  const rightCstic = cstics[InchCstics.SHAFT_MACHINING_RIGHT];
  if (showChamferedSection(rightCstic.value)) {
    right.chamfer = resolveChamferSection(cstics, t, chamfer.right);
  }
  data.right = right;

  return data;
}

function resolveChamferSection(
  cstics: Dict<LinearCstic>,
  t: WithTranslation["t"],
  config: (typeof chamfer)["left"],
): ChamferSummaryData {
  const angle = cstics[config.angle]?.value ?? "";
  let width;
  if (isChamferManualWidth(angle)) {
    width = cstics[config.width.sf]?.value + InchCstics.UNIT;
  } else {
    width = cstics[config.width.nf]?.value;
  }
  const angleNumber = angle?.substring(2);
  const angleLabel = t(`configurator.shaft.angle.${angleNumber}`);
  return {
    angle: { name: t("configurator.shaft.chamferAngle.title"), value: angleLabel },
    width: { name: t("configurator.shaft.chamferWidth.title.default"), value: width ?? "" },
  };
}

function resolveRadialThreadSection(cstics: Dict<LinearCstic>, t: WithTranslation["t"], config: typeof radialThread) {
  const data: Partial<RadialThreadSummaryData> = {};

  const pdt = cstics[config.pdt]?.value ?? "";
  data.pdt = { name: resolveMachiningLabelString(ConfiguratorType.InchShaft, t, "980"), value: pdt };

  const threadSpacingInch = cstics[config.threadSpacing.inch]?.value ?? "";
  const threadSpacingMm = cstics[config.threadSpacing.metric]?.value ?? "";
  data.threadSpacing = {
    name: t("configurator.shaft.radialThreadSpacing.title.default"),
    value: formatDualValue({ inch: threadSpacingInch, mm: threadSpacingMm }),
  };

  const aLInch = cstics[config.aL.inch.value]?.value ?? "";
  const aLMm = cstics[config.aL.metric.value]?.value ?? "";
  data.aL = {
    name: t("configurator.shaft.aL.title"),
    value: formatDualValue({ inch: aLInch, mm: aLMm }),
  };

  const aRInch = cstics[config.aR.inch.value]?.value ?? "";
  const aRMm = cstics[config.aR.metric.value]?.value ?? "";
  data.aR = {
    name: t("configurator.shaft.aR.title"),
    value: formatDualValue({ inch: aRInch, mm: aRMm }),
  };

  const threadHoleAmount = cstics[config.threadHoleAmount]?.value ?? "";
  data.threadHoleAmount = {
    name: t("configurator.shaft.step.2.substep.3.extraFields.holeQuantity.title"),
    value: (+threadHoleAmount).toFixed(0),
  };

  const threadDepth = (cstics[config.threadDepth]?.value ?? "") + INCH_UNIT;
  data.threadDepth = { name: t("configurator.shaft.radialThreadDepth.title.default"), value: threadDepth };

  return data as RadialThreadSummaryData;
}

function getMachiningLeft(cstics: Dict<LinearCstic>, t: WithTranslation["t"]): SummaryData | null {
  const leftCstic = cstics[InchCstics.SHAFT_MACHINING_LEFT];
  if (leftCstic?.value) {
    const selectedValue = leftCstic.values.find((v) => v.value === leftCstic.value)!;
    return {
      name: t("configurator.shaft.step.2.substep.3.sectionTitle.left"),
      isStandard: !!selectedValue.standard,
      value: selectedValue.value,
      extra: resolveMachiningLabelString(ConfiguratorType.InchShaft, t, selectedValue.value),
    };
  }
  return null;
}

function getMachiningMiddle(cstics: Dict<LinearCstic>, t: WithTranslation["t"]): SummaryData | null {
  const middleCstic = cstics[InchCstics.SHAFT_MACHINING_MIDDLE];
  if (middleCstic?.value) {
    const selectedValue = middleCstic.values.find((v) => v.value === middleCstic.value)!;
    return {
      name: t("configurator.shaft.step.2.substep.3.sectionTitle.middle"),
      isStandard: !!selectedValue.standard,
      value: selectedValue.value,
      extra: resolveMachiningLabelString(ConfiguratorType.InchShaft, t, selectedValue.value),
    };
  }
  return null;
}

function getMachiningRight(cstics: Dict<LinearCstic>, t: WithTranslation["t"]): SummaryData | null {
  const rightCstic = cstics[InchCstics.SHAFT_MACHINING_RIGHT];
  if (rightCstic?.value) {
    const selectedValue = rightCstic.values.find((v) => v.value === rightCstic.value)!;
    return {
      name: t("configurator.shaft.step.2.substep.3.sectionTitle.right"),
      isStandard: !!selectedValue.standard,
      value: selectedValue.value,
      extra: resolveMachiningLabelString(ConfiguratorType.InchShaft, t, selectedValue.value),
    };
  }
  return null;
}
